import React, { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import "moment/locale/es";
import StyledTable from "../../common/StyledTable";
import EditConcursoModal from "../common/EditConcursoModal";
import DraftSendModal from "./DraftSendModal";
import DraftDeleteModal from "./DraftDeleteModal";
import TreeView from "@material-ui/lab/TreeView";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import TreeItem from "@material-ui/lab/TreeItem";
import { makeStyles } from "@material-ui/core/styles";
import { handleLoadingConcursoError } from "../../utils/errors";

const useStyles = makeStyles({
  item: {
    "& .MuiTreeItem-iconContainer svg": { fontSize: "24px" },
    "& .MuiTreeItem-label": { fontSize: "24px" },
    "&.Mui-selected .MuiTreeItem-label": { background: "none !important" },
  },
});

type ConcursosFacultadProps = {
  visible: boolean;
  loading: boolean;
  concursosFacultad: ConcursosFacultad[];
};

type Concurso = {
  id: number;
  codigo: string;
  nombre: string;
  fecha_inicio: string;
  fecha_termino: string;
  fecha_aviso_diario: string;
  id_facultad: number;
  id_usuario_creador: number;
  id_usuario_contacto: number;
};

type concursoDraft = {
  concurso: Concurso;
  fecha_ultima_modificacion: string;
  estado: string;
  comentario: string;
};

type ConcursosFacultad = {
  nombre_facultad: string;
  data: concursoDraft[];
};

type Facultad = {
  id: number;
  nombre: string;
};

const ConcursosFacultad: React.FC<ConcursosFacultadProps> = (props) => {
  const classes = useStyles();
  if (props.loading) {
    return <StyledTable loading />;
  }
  if (!props.visible) {
    return (
      <div
        style={{
          paddingTop: "1em",
          paddingLeft: "2em",
          paddingRight: "2em",
        }}
      >
        <p>No hay concursos disponibles.</p>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div>
        {props.concursosFacultad.map(
          (concursoFacultad: ConcursosFacultad, index) => {
            return (
              <React.Fragment key={index}>
                <div
                  style={{
                    paddingLeft: "2em",
                    paddingRight: "2em",
                  }}
                >
                  <TreeItem
                    nodeId={concursoFacultad.nombre_facultad}
                    className={classes.item}
                    label={
                      concursoFacultad.nombre_facultad
                        ? concursoFacultad.nombre_facultad
                        : ""
                    }
                  >
                    <div
                      id={concursoFacultad.nombre_facultad + "LT"}
                      className="mt-3"
                    >
                      <Concurso
                        key={concursoFacultad.nombre_facultad}
                        rows={concursoFacultad.data.map(
                          (concursoDraft: concursoDraft) =>
                            filaConcurso({
                              codigo: concursoDraft.concurso.codigo,
                              nombre: concursoDraft.concurso.nombre,
                              apertura: concursoDraft.concurso.fecha_inicio,
                              cierre: concursoDraft.concurso.fecha_termino,
                              concursoId: concursoDraft.concurso.id,
                              creador:
                                concursoDraft.concurso.id_usuario_creador,
                              contacto:
                                concursoDraft.concurso.id_usuario_contacto,
                              publicacion:
                                concursoDraft.concurso.fecha_aviso_diario,
                              fecha_ultima_modificacion:
                                concursoDraft.fecha_ultima_modificacion,
                              comentarios: concursoDraft.comentario,
                              estado: concursoDraft.estado,
                            })
                        )}
                      />
                    </div>
                  </TreeItem>
                </div>
              </React.Fragment>
            );
          }
        )}
      </div>
    </React.Fragment>
  );
};

type ConcursoProps = {
  rows: ({ content: string } | { content: JSX.Element })[][];
};

const Concurso: React.FC<ConcursoProps> = (props) => {
  const rows = props.rows;
  const headers = [
    { content: "Código" },
    { content: "Nombre" },
    //{ content: "Fecha de Publicación en el Diario" },
    { content: "Última Modificación" },
    { content: "Estado" },
    { content: "Acciones" },
  ];

  return <StyledTable headers={headers} rows={rows} />;
};

const filaConcurso = (props: FilaConcursoProps) => {
  const fechaUltimaModificacion = moment(props.fecha_ultima_modificacion)
    .locale("es")
    .format("LLL");

  const getVariant = (estado: string) => {
    switch (estado) {
      case "rechazado":
        return <strong style={{ color: "#f50057" }}>Rechazado</strong>;
      case "no_publicado":
        return <em>No enviado</em>;
      default:
        return <em>No enviado</em>;
    }
  };

  return [
    { content: props.codigo, style: { width: "1px" } },
    { content: props.nombre },
    //{ content: fechaPublicacion, style: {width: "254px"} },
    { content: fechaUltimaModificacion, style: { width: "214px" } },
    { content: getVariant(props.estado), style: { width: "113px" } },
    {
      content: (
        <div className="d-flex justify-content-between">
          <EditConcursoModal
            label="Editar"
            concursoId={props.concursoId}
            comentarios={props.comentarios}
            sitio={"creador"}
          />
          <DraftSendModal label="Enviar" concursoId={props.concursoId} />
          <DraftDeleteModal
            label="Archivar"
            concursoId={props.concursoId}
            name={`${props.codigo} - ${props.nombre}`}
          />
        </div>
      ),
      style: { width: "180px" },
    },
  ];
};

type FilaConcursoProps = {
  codigo: string;
  nombre: string;
  apertura: string;
  cierre: string;
  concursoId: number;
  creador: number;
  contacto: number;
  publicacion: string;
  fecha_ultima_modificacion: string;
  comentarios: string;
  estado: string;
};

type DraftProps = {
  index: number;
};

const ConcursosDraft: React.FC<DraftProps> = (props) => {
  const [concursos, setConcursos] = useState([]);
  const [loadingConcursos, setLoadingConcursos] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = React.useState([]);
  
  const handleToggle = (event, nodeIds) => {
    setExpanded(nodeIds);
  };

  useEffect(() => {
    /**
         function sortByFechaPublicacion(data: []) {
            let sortedData = data.sort((a: any, b: any) => new Date(b.fecha_aviso_diario).getTime() - new Date(a.fecha_aviso_diario).getTime());
            return sortedData;
        }*/

    function handleError(error: any): void {
      setError(true);
      handleLoadingConcursoError(
        error,
        (window as any).ENV.URL_FRONT + "Concursos/?tab=" + props.index
      );
    }

    let api_route = "/concursosBorrador/";

    axios
      .get((window as any).ENV.API_URL + api_route)
      .then((res) => {
        setConcursos(res.data);
        setExpanded(res.data.map((concurso) => concurso.nombre_facultad));
        setLoadingConcursos(false);
      })
      .catch((error) => handleError(error));
  }, []);

  if (!error) {
    return (
        <TreeView
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
          expanded={expanded}
          onNodeToggle={handleToggle}
        >
          <ConcursosFacultad
            loading={loadingConcursos}
            visible={concursos.length > 0}
            concursosFacultad={concursos}
          />
        </TreeView>
    );
  }

  return null;
};

export default ConcursosDraft;
